// frontend/admin-portal/src/components/suppliers/SupplierModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Alert } from "@fiberhome/shared-ui";
import { supplierService } from "../../services/supplierService";

const EMPTY = {
  name: "",
  slug: "",
  code: "",
  contactName: "",
  phone: "",
  altPhone: "",
  email: "",
  website: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  region: "",
  country: "",
  postalCode: "",
  taxId: "",
  registrationNo: "",
  paymentTermsDays: 30,
  creditLimit: 0,
  currency: "IQD",
  bankName: "",
  bankAccount: "",
  bankIban: "",
  notes: "",
  rating: 0,
  isActive: true,
};

export default function SupplierModal({ open, supplier, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("basic");

  useEffect(() => {
    if (!open) return;
    setTab("basic");
    setError("");
    setForm(supplier ? { ...EMPTY, ...supplier } : EMPTY);
  }, [open, supplier]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        paymentTermsDays: Number(form.paymentTermsDays) || 0,
        creditLimit: Number(form.creditLimit) || 0,
        rating: Number(form.rating) || 0,
      };
      if (supplier?.id) await supplierService.update(supplier.id, payload);
      else await supplierService.create(payload);
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!supplier?.id;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? `Edit: ${supplier?.name}` : "New Supplier"}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={saving}>
            {isEditing ? "Save Changes" : "Create Supplier"}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="fh-supplier-tabs">
        {[
          { id: "basic", label: "Basic" },
          { id: "contact", label: "Contact" },
          { id: "address", label: "Address" },
          { id: "business", label: "Business" },
          { id: "banking", label: "Banking" },
          { id: "notes", label: "Notes" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            className={`fh-supplier-tab ${tab === t.id ? "is-active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-3">
        {tab === "basic" && (
          <div className="row g-3">
            <div className="col-md-8">
              <Input
                label="Name *"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <Input
                label="Code"
                value={form.code}
                onChange={(e) => set("code", e.target.value)}
                hint="Leave empty to auto-generate"
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Slug"
                value={form.slug}
                onChange={(e) => set("slug", e.target.value)}
                hint="Auto from name if empty"
              />
            </div>
            <div className="col-md-3">
              <Input
                label="Currency"
                value={form.currency}
                onChange={(e) => set("currency", e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <Input
                label="Rating (0–5)"
                type="number"
                min={0}
                max={5}
                value={form.rating}
                onChange={(e) => set("rating", e.target.value)}
              />
            </div>
            <div className="col-12">
              <label className="fh-checkbox">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => set("isActive", e.target.checked)}
                />
                <span>Active</span>
              </label>
            </div>
          </div>
        )}

        {tab === "contact" && (
          <div className="row g-3">
            <div className="col-md-6">
              <Input
                label="Contact Person"
                value={form.contactName}
                onChange={(e) => set("contactName", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <Input
                label="Phone"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <Input
                label="Alt Phone"
                value={form.altPhone}
                onChange={(e) => set("altPhone", e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <Input
                label="Website"
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
              />
            </div>
          </div>
        )}

        {tab === "address" && (
          <div className="row g-3">
            <div className="col-12">
              <Input
                label="Address Line 1"
                value={form.addressLine1}
                onChange={(e) => set("addressLine1", e.target.value)}
              />
            </div>
            <div className="col-12">
              <Input
                label="Address Line 2"
                value={form.addressLine2}
                onChange={(e) => set("addressLine2", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Input
                label="City"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Region / State"
                value={form.region}
                onChange={(e) => set("region", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Country"
                value={form.country}
                onChange={(e) => set("country", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Postal Code"
                value={form.postalCode}
                onChange={(e) => set("postalCode", e.target.value)}
              />
            </div>
          </div>
        )}

        {tab === "business" && (
          <div className="row g-3">
            <div className="col-md-6">
              <Input
                label="Tax ID / VAT"
                value={form.taxId}
                onChange={(e) => set("taxId", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Registration No."
                value={form.registrationNo}
                onChange={(e) => set("registrationNo", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Payment Terms (days)"
                type="number"
                min={0}
                value={form.paymentTermsDays}
                onChange={(e) => set("paymentTermsDays", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Credit Limit"
                type="number"
                min={0}
                value={form.creditLimit}
                onChange={(e) => set("creditLimit", e.target.value)}
              />
            </div>
          </div>
        )}

        {tab === "banking" && (
          <div className="row g-3">
            <div className="col-md-6">
              <Input
                label="Bank Name"
                value={form.bankName}
                onChange={(e) => set("bankName", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Account No."
                value={form.bankAccount}
                onChange={(e) => set("bankAccount", e.target.value)}
              />
            </div>
            <div className="col-12">
              <Input
                label="IBAN"
                value={form.bankIban}
                onChange={(e) => set("bankIban", e.target.value)}
              />
            </div>
          </div>
        )}

        {tab === "notes" && (
          <Input
            label="Notes"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        )}
      </div>
    </Modal>
  );
}
